import { useAuthContext } from "providers/Auth";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";

import Drawer from "react-modern-drawer";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  CheckboxSize,
  Show,
  Input,
  InputSize,
  TextSkeleton,
  IconButton,
  IconButtonVariant,
  IconButtonSize
} from "shared/components";

import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

import { Avatar } from "shared/components/Avatar";
import moment from "moment";

import { toast } from "react-toastify";

import {
  DueFilterMapping,
  CreatedAtFilterMapping,
  DropdownLabels,
  RelatedPayloadMapping,
  noOffsetDate
} from "../constants";
import { api } from "api/task";
import { getErrorMessage } from "utils/error";
import {
  GetTasksParams,
  GetCountsBySubjectType,
  DueDateFilterOptions,
  CreatedAtOptions,
  TaskSubjectPayload
} from "../types";
import { DateFilterSection } from "../components/DateFilterSection";

type FiltersDrawerProps = {
  open: boolean;
  onClose?: () => void;
  onSave?: (filters: Partial<GetTasksParams>) => void;
  startDueDate?: Date;
  endDueDate?: Date;
  startCreatedAtDate?: Date;
  endCreatedAtDate?: Date;
  filters?: Partial<GetTasksParams>;
  taskParams?: GetTasksParams;
};

const applyDateFilter = <K extends string>(
  filter: { from: string; to: string } | undefined,
  mapping: Record<string, { from: string; to: string }>,
  setKind: (kind: K) => void,
  setInputValues: (values: { start_date: string; end_date: string }) => void,
  customKind: K
) => {
  if (!filter) return;
  const matchingKey = Object.entries(mapping).find(
    ([_, m]) => m.from === filter.from && m.to === filter.to
  )?.[0];
  if (matchingKey) setKind(matchingKey as K);
  else {
    setKind(customKind);
    setInputValues({
      start_date: noOffsetDate(filter.from),
      end_date: noOffsetDate(filter.to)
    });
  }
};

const handleRelatedFilterChange = (
  typedSubject: TaskSubjectPayload,
  relatedToFilters: TaskSubjectPayload[],
  setRelatedToFilters: (filters: TaskSubjectPayload[]) => void
) => {
  setRelatedToFilters(
    relatedToFilters.includes(typedSubject)
      ? relatedToFilters.filter((t) => t !== typedSubject)
      : [...relatedToFilters, typedSubject]
  );
};

export const FiltersDrawer = ({
  open,
  onClose,
  onSave,
  startDueDate,
  endDueDate,
  startCreatedAtDate,
  endCreatedAtDate,
  filters,
  taskParams
}: FiltersDrawerProps) => {
  const { auth } = useAuthContext();
  const [relatedToFilters, setRelatedToFilters] = useState<
    TaskSubjectPayload[]
  >([]);

  const [dueKind, setDueKind] = useState<DueDateFilterOptions>(
    DueDateFilterOptions.NONE
  );
  const [dueInputValues, setDueInputValues] = useState<{
    start_date: string;
    end_date: string;
  }>({
    start_date: noOffsetDate(startDueDate || new Date()),
    end_date: noOffsetDate(endDueDate || new Date())
  });

  const [createdAtKind, setCreatedAtKind] = useState<CreatedAtOptions>(
    CreatedAtOptions.NONE
  );
  const [createdAtInputValues, setCreatedAtInputValues] = useState<{
    start_date: string;
    end_date: string;
  }>({
    start_date: noOffsetDate(startCreatedAtDate || new Date()),
    end_date: noOffsetDate(endCreatedAtDate || new Date())
  });
  const [createdByIds, setCreatedByIds] = useState<Array<number>>([]);
  const [createdBySearch, setCreatedBySearch] = useState("");
  const [saving, setSaving] = useState(false);

  const [debouncedSearch] = useDebouncedValue(createdBySearch, 500);

  const countParams: Partial<GetCountsBySubjectType> | undefined = taskParams
    ? {
        statuses: taskParams.statuses,
        assignees: taskParams.assignees,
        creators: taskParams.creators,
        created_at: taskParams.created_at,
        due_date: taskParams.due_date
      }
    : undefined;

  const { data: count, isLoading: loadingCount } = useQuery({
    queryKey: ["TASK_COUNTS", countParams],
    queryFn: () => api.getCountBySubjectType(countParams),
    enabled: open,
    select: (data) => ({
      leads_count: data.leads_count,
      applications_count: data.applications_count
    })
  });

  const { data: creators, isLoading: loadingCreators } = useQuery({
    queryKey: ["TASK_CREATORS", debouncedSearch],
    queryFn: () => api.getCreators(debouncedSearch),
    enabled: open
  });

  const loading = loadingCount || loadingCreators;

  useEffect(() => {
    if (!open) return;

    if (filters?.subjects)
      setRelatedToFilters(filters.subjects.map((s) => s.subject_type));

    if (filters?.creators)
      setCreatedByIds(
        filters.creators
          .filter((c) => c.subject_type === "partner_user")
          .map((c) => Number(c.subject_id))
      );

    applyDateFilter(
      filters?.due_date,
      DueFilterMapping,
      setDueKind,
      setDueInputValues,
      DueDateFilterOptions.CUSTOM
    );

    applyDateFilter(
      filters?.created_at,
      CreatedAtFilterMapping,
      setCreatedAtKind,
      setCreatedAtInputValues,
      CreatedAtOptions.CUSTOM
    );
  }, [open, filters]);

  const filteredAssignees = useMemo(() => {
    const allCreators = creators || [];
    const sorted = allCreators.toSorted((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
    if (auth) {
      const withoutAuth = sorted.filter((a) => a.id !== auth.id);
      return [auth, ...withoutAuth];
    }
    return sorted;
  }, [creators, auth]);

  const handleCreatedByChange = (userId: number, isSelected: boolean) => {
    setCreatedByIds((prev) =>
      isSelected
        ? prev.filter((id) => Number(id) !== Number(userId))
        : [...prev, userId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const filters: Partial<GetTasksParams> = {
        subjects:
          relatedToFilters.length > 0
            ? relatedToFilters.map((type) => ({ subject_type: type }))
            : undefined
      };

      if (dueKind !== DueDateFilterOptions.NONE && dueKind in DueFilterMapping)
        filters.due_date = DueFilterMapping[dueKind];
      else if (
        dueKind === DueDateFilterOptions.CUSTOM &&
        dueInputValues.end_date &&
        dueInputValues.start_date
      )
        filters.due_date = {
          from: moment
            .utc(dueInputValues.start_date)
            .startOf("day")
            .toISOString(),
          to: moment.utc(dueInputValues.end_date).endOf("day").toISOString()
        };

      if (
        createdAtKind !== CreatedAtOptions.NONE &&
        createdAtKind in CreatedAtFilterMapping
      )
        filters.created_at = CreatedAtFilterMapping[createdAtKind];
      else if (
        createdAtKind === CreatedAtOptions.CUSTOM &&
        createdAtInputValues.end_date &&
        createdAtInputValues.start_date
      )
        filters.created_at = {
          from: moment
            .utc(createdAtInputValues.start_date)
            .startOf("day")
            .toISOString(),
          to: moment
            .utc(createdAtInputValues.end_date)
            .endOf("day")
            .toISOString()
        };

      if (createdByIds.length)
        filters.creators = createdByIds.map((id) => ({
          subject_type: "partner_user",
          subject_id: Number(id)
        }));

      onSave?.(filters);

      onClose?.();
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      className="bg-background-1000 border-alpha-100 rounded-lg border"
      direction="right"
      size={360}
      open={open}
      onClose={onClose}
      lockBackgroundScroll
    >
      <div className="flex h-screen flex-col">
        <div className="border-b border-alpha-40 px-6 py-4 flex items-center justify-between">
          <div className="w-[207px]">
            <h1 className="text-content-1000 text-xl font-bold">Filters</h1>
          </div>
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.LG}
            HugeIcon={Cancel01Icon}
            onClick={onClose}
            aria-label="Close drawer"
          />
        </div>

        <div className="px-6 pt-4 bg-background-1000">
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.BASE}
            onClick={() => {
              setRelatedToFilters([]);
              setDueKind(DueDateFilterOptions.NONE);
              setCreatedAtKind(CreatedAtOptions.NONE);
              setCreatedByIds([]);
            }}
          >
            Unselect all
          </Button>
        </div>

        <div className="flex-1 space-y-4 px-6 py-4">
          <div className="border border-alpha-40 w-[328px] text-center -mx-2" />

          <div className="space-y-2">
            <span className="text-content-1000 font-bold text-base">
              {DropdownLabels.related}
            </span>
            <div className="space-y-2">
              {Object.entries(RelatedPayloadMapping).map(([subject, label]) => {
                const typedSubject = subject as TaskSubjectPayload;
                const countKey =
                  typedSubject === TaskSubjectPayload.CRM_LEADS
                    ? "leads_count"
                    : "applications_count";
                return (
                  <div key={subject} className="flex gap-2 items-center">
                    <Checkbox
                      onChange={() => {
                        handleRelatedFilterChange(
                          typedSubject,
                          relatedToFilters,
                          setRelatedToFilters
                        );
                      }}
                      checked={relatedToFilters.includes(typedSubject)}
                      size={CheckboxSize.SM}
                      disabled={loading}
                      wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                    />
                    <span className="text-content-700 font-medium text-sm">
                      {label} ({count?.[countKey] ?? 0})
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <DateFilterSection
            filterKey="due"
            kind={dueKind}
            inputValues={dueInputValues}
            onChange={(kind, inputValues) => {
              setDueKind(kind);
              setDueInputValues(inputValues);
            }}
          />

          <DateFilterSection
            filterKey="created_at"
            kind={createdAtKind}
            inputValues={createdAtInputValues}
            onChange={(kind, inputValues) => {
              setCreatedAtKind(kind);
              setCreatedAtInputValues(inputValues);
            }}
          />

          <div className="border border-alpha-40 w-[328px] text-center -mx-2" />
          <div className="space-y-2">
            <span className="text-sm text-content-1000 font-bold">
              Created by
            </span>
            <Input
              id="created-by-search"
              placeholder="Search..."
              size={InputSize.BASE}
              value={createdBySearch}
              onChange={(e) => setCreatedBySearch(e.target.value)}
              wrapperClassName="w-full"
              className="placeholder:text-content-100 placeholder:font-medium !bg-content-100-inverted !rounded-lg !mb-0 !px-[14px] !py-2.5 !shadow-xs"
            />
            <Show if={loading}>
              <div className="flex w-full items-center justify-start gap-3 h-[44px]">
                <div className="w-[20px] h-[20px] bg-alpha-40 rounded" />
                <div className="h-[20px] w-[20px] bg-alpha-40 rounded-full flex-shrink-0" />
                <div className="flex-1">
                  <TextSkeleton size="base" className="w-[150px]" />
                </div>
              </div>
            </Show>
            <Show if={!loading && !creators?.length}>
              <div className="py-2.5 text-base font-medium text-content-1000">
                No users found
              </div>
            </Show>
            <Show
              if={!loading && filteredAssignees.length === 0 && createdBySearch}
            >
              <div className="py-2.5 text-base font-medium text-content-1000">
                No matching users
              </div>
            </Show>
            <div className="flex flex-col">
              {filteredAssignees.map((user) => {
                const isSelected = createdByIds
                  .map(Number)
                  .includes(Number(user.id));
                return (
                  <div
                    key={user.id}
                    className="flex w-full items-center justify-start gap-3 h-[44px] py-2.5"
                  >
                    <Checkbox
                      onChange={() =>
                        handleCreatedByChange(user.id, isSelected)
                      }
                      checked={isSelected}
                      size={CheckboxSize.SM}
                      wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                    />
                    <Avatar
                      name={user.name}
                      className="text-[9px] text-content-1000"
                      size={20}
                    />
                    <p className="flex items-center text-content-1000 text-base font-medium">
                      {user.name}
                      {user.id === auth!.id ? (
                        <span className="text-content-200">&nbsp;(You)</span>
                      ) : null}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-t border-alpha-100 px-6 py-4">
          <div className="flex justify-between gap-2">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.BASE}
              className="w-full shadow-xs !py-2.5 !px-4.5"
              onClick={onClose}
            >
              <span className="text-base font-bold text-content-1000">
                Cancel
              </span>
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.BASE}
              className="w-full shadow-xs !py-2.5 !px-4.5"
              onClick={handleSave}
              disabled={loading}
              loading={saving}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
